import { Suspense, lazy, useCallback, useEffect } from "react";
import { useHistory } from "react-router";

import { markResultsViewed } from "../../../utils/csatSurvey";
import { isAtInitialPage } from "../../../utils/navigation";
import { closeNubankWebview } from "../../../utils/nubankHandoff";

// `flights` is lazy-loaded by FlightsRouteComponent, so it must not be imported
// statically from this eagerly-bundled slot (@nx/enforce-module-boundaries).
// Reuse the already-loaded chunk via a dynamic import.
const FlightShopProgressHeader = lazy(() =>
  import("@hopper-b2b/flights").then((m) => ({
    default: m.FlightShopProgressHeader,
  }))
);

export interface NubankFlightShopMobileHeaderProps {
  assets?: Record<string, any>;
  className?: string;
  isFilterHidden?: boolean;
  isTripEditHidden?: boolean;
  onFiltersClick?: () => void;
}

/**
 * Nubank override for the `flight-shop-mobile-header` slot.
 *
 * On the flights shop/search page the back button normally calls
 * `history.goBack()`. When the user arrived via a deeplink (e.g. the Price
 * Alert push notification), there is no in-app history, so `goBack()` is a
 * no-op and the button appears broken (NVH-733). In that case we close the
 * native Nubank webview instead, handing control back to the app. Otherwise we
 * keep the default in-app back navigation.
 */
export const NubankFlightShopMobileHeader = (
  props: NubankFlightShopMobileHeaderProps
) => {
  const history = useHistory();

  useEffect(() => {
    markResultsViewed("flights");
  }, []);

  const handleGoBack = useCallback(() => {
    if (isAtInitialPage()) {
      closeNubankWebview();
    } else {
      history.goBack();
    }
  }, [history]);

  return (
    <div className="progress-header-menu-wrapper">
      <Suspense fallback={null}>
        <FlightShopProgressHeader
          {...props}
          onFiltersClick={props.onFiltersClick ?? (() => undefined)}
          onGoBack={handleGoBack}
        />
      </Suspense>
    </div>
  );
};
